import type { AxiosError } from 'axios';

import { toUserMessage } from '@/lib/api/errors';
/**
 * API Error response structure
 */
interface APIErrorResponse {
  detail?: string;
  message?: string;
  error?: {
    message: string;
    type?: string;
    param?: string;
    code?: string;
  };
}

/**
 * Error handling result
 */
export interface ErrorHandlingResult {
  message: string;
  shouldRetry: boolean;
  statusCode?: number;
}

/**
 * Get user-friendly error message based on HTTP status code and error response
 * @param error - Axios error object
 * @param context - Context for the error (e.g., 'enable base model', 'create custom model')
 * @returns Error handling result with message and retry flag
 */
export function getErrorMessage(error: unknown, context?: string): ErrorHandlingResult {
  // Handle non-Axios errors.
  //
  // Not `error.message`: this branch only runs for things the API did not throw
  // — a TypeError, a bug — whose message ("Cannot read properties of undefined")
  // is for the console, not a user. Backend messages are a separate question and
  // are still surfaced below, deliberately: asab-gateway classifies which errors
  // are user-actionable (see isCustomModelError / mapProviderStatus in
  // internal/handlers/errors.go — 4xx passes through, 5xx is masked as 502), so
  // a 4xx body here is meant for the user and is often more specific than
  // anything this file could write.
  if (!isAxiosError(error)) {
    return {
      message: toUserMessage(error, 'An unexpected error occurred'),
      shouldRetry: false,
    };
  }

  const axiosError = error as AxiosError<APIErrorResponse>;
  const statusCode = axiosError.response?.status;
  const errorData = axiosError.response?.data;

  // Extract error message from response
  const apiMessage =
    errorData?.detail || errorData?.message || errorData?.error?.message || '';

  // Handle specific status codes
  switch (statusCode) {
    case 400:
      return {
        message: apiMessage || 'Invalid request. Please check your input and try again.',
        shouldRetry: false,
        statusCode,
      };

    case 401:
      return {
        message: 'Your session has expired. Please log in again.',
        shouldRetry: false,
        statusCode,
      };

    case 403:
      return {
        message:
          apiMessage || "You don't have permission to perform this action. Please contact your administrator.",
        shouldRetry: false,
        statusCode,
      };

    case 404:
      return {
        message: apiMessage || `The requested ${context || 'resource'} was not found.`,
        shouldRetry: false,
        statusCode,
      };

    case 409:
      return {
        message:
          apiMessage ||
          'A conflict occurred. This resource may already exist or is in use.',
        shouldRetry: false,
        statusCode,
      };

    case 422:
      return {
        message: apiMessage || 'Validation failed. Please check your input.',
        shouldRetry: false,
        statusCode,
      };

    case 429:
      return {
        message:
          'Too many requests. Please wait a moment before trying again.',
        shouldRetry: true,
        statusCode,
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        message:
          apiMessage ||
          'A server error occurred. Please try again later or contact support if the problem persists.',
        shouldRetry: true,
        statusCode,
      };

    default:
      // Network errors (no response)
      if (!axiosError.response) {
        return {
          message:
            'Network error. Please check your connection and try again.',
          shouldRetry: true,
        };
      }

      // Other client errors (4xx)
      if (statusCode && statusCode >= 400 && statusCode < 500) {
        return {
          message: apiMessage || 'An error occurred. Please try again.',
          shouldRetry: false,
          statusCode,
        };
      }

      // Unknown errors
      return {
        message: apiMessage || 'An unexpected error occurred. Please try again.',
        shouldRetry: false,
        statusCode,
      };
  }
}

/**
 * Type guard to check if error is an Axios error
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Get context-specific error message for model operations
 */
export function getModelOperationError(
  error: unknown,
  operation: 'fetch' | 'create' | 'update' | 'delete' | 'enable' | 'disable' | 'add',
  modelType: 'base model' | 'custom model' | 'ASAB model'
): ErrorHandlingResult {
  const result = getErrorMessage(error);

  // Customize message based on operation if no specific API message
  if (!result.message.includes(modelType)) {
    const operationText = {
      fetch: 'fetch',
      create: 'create',
      update: 'update',
      delete: 'delete',
      enable: 'enable',
      disable: 'disable',
      add: 'add',
    }[operation];

    // Only override generic messages
    if (
      result.message.includes('An error occurred') ||
      result.message.includes('An unexpected error')
    ) {
      result.message = `Failed to ${operationText} ${modelType}. ${result.message}`;
    }
  }

  return result;
}

/**
 * Format validation errors from 422 responses
 */
export function formatValidationErrors(error: unknown): Record<string, string> {
  if (!isAxiosError(error)) {
    return {};
  }

  const axiosError = error as AxiosError<APIErrorResponse>;
  const errorData = axiosError.response?.data;

  // Handle different validation error formats
  if (errorData?.error?.param) {
    return {
      [errorData.error.param]: errorData.error.message,
    };
  }

  // Return empty object if no validation errors found
  return {};
}
